"use client";

import React from "react";
import { Smartphone, Battery, Cpu, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const troubleshootingSteps = [{
  id: "phone",
  title: "Phone & App",
  description: "If the app is unresponsive, force close it and verify your internet connection before scanning again.",
  icon: Smartphone,
  statusColor: "bg-destructive",
  statusLabel: "Check Connection"
}, {
  id: "powerbank",
  title: "Powerbank",
  description: "Not charging? Ensure the built-in cable is firmly plugged into your device's charging port.",
  icon: Battery,
  statusColor: "bg-destructive",
  statusLabel: "Check Cable"
}, {
  id: "station",
  title: "Charging Station",
  description: "If a powerbank isn't released, wait 30 seconds or try scanning a different available slot.",
  icon: Cpu,
  statusColor: "bg-destructive",
  statusLabel: "Check Station"
}];
export default function Process() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="process" className="py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div ref={ref} className={`flex flex-col items-center text-center mb-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Quick Troubleshooting
          </h2>
          <p className="text-muted-foreground max-w-2xl text-lg">
            Experiencing an issue? Check these common indicators first to get back to charging quickly.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          {}
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-border/50 -z-10" />

          {troubleshootingSteps.map((step, i) => {
          const Icon = step.icon;
          return <div key={step.id} data-index={i} className={`transition-all duration-700 ease-out delay-${i * 200} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                <Card className="h-full border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-colors duration-300 relative overflow-visible mt-8 md:mt-0">
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2">
                    <div className="relative flex items-center justify-center w-16 h-16 rounded-2xl bg-background border border-border shadow-sm">
                      <Icon className="w-8 h-8 text-foreground" />
                      {}
                      <div className="absolute -top-1 -right-1 flex h-4 w-4">
                        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${step.statusColor}`}></span>
                        <span className={`relative inline-flex rounded-full h-4 w-4 border-2 border-background ${step.statusColor}`}></span>
                      </div>
                    </div>
                  </div>

                  <CardHeader className="pt-12 text-center pb-2">
                    <div className="flex justify-center mb-2">
                      <span className="text-xs font-medium uppercase tracking-wider text-destructive flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${step.statusColor}`} />
                        {step.statusLabel}
                      </span>
                    </div>
                    <CardTitle className="text-xl">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <CardDescription className="text-base">
                      {step.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className={`mt-16 flex justify-center transition-all duration-700 delay-500 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Button asChild size="lg" variant="outline" className="rounded-full px-8">
            <Link to="/SupportPage">
              Still need help? Contact Support
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}